<script>
import { mapState, mapGetters } from 'vuex';
import LockIcon from '@/components/app/LockIcon.vue';
import Back from '@/assets/icons/back-large.svg';
import Heart from '@/assets/icons/heart.svg';
import HeartFilled from '@/assets/icons/heart-filled.svg';
import More from '@/assets/icons/more-vertical.svg';
import Headphones from '@/assets/icons/headphones.svg';
import CardText from '@/assets/icons/card-text.svg';

const HelpTooltip = () =>
  import(/* webpackChunkName: "help-tooltip" */ '@/components/app/HelpTooltip.vue');
// const ShareContentMenuItem = () =>
// import(/* webpackChunkName: "share-menu-item" */ '@/components/app/ShareContentMenuItem.vue');
const StackAddModal = () =>
  import(/* webpackChunkName: "stack-add-modal" */ '@/components/app/StackAddModal.vue');
const StackCreateModal = () =>
  import(/* webpackChunkName: "stack-create-modal" */ '@/components/app/StackCreateModal.vue');
const StackEditModal = () =>
  import(/* webpackChunkName: "stack-edit-modal" */ '@/components/app/StackEditModal.vue');
const StackDeleteModal = () =>
  import(/* webpackChunkName: "stack-delete-modal" */ '@/components/app/StackDeleteModal.vue');

// IMPORTANT NOTE: Tooltips used fixed positioning from props
// If top bar icons move, update tooltip props

export default {
  name: 'TopBar',

  components: {
    HelpTooltip,
    // ShareContentMenuItem,
    StackAddModal,
    StackCreateModal,
    StackEditModal,
    StackDeleteModal,
    LockIcon,
    Back,
    Heart,
    HeartFilled,
    More,
    Headphones,
    CardText,
  },

  props: {
    safeArea: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      showModal: '',
    };
  },

  computed: {
    ...mapState('ui', ['currentId', 'routeName', 'helpTooltips']),

    ...mapGetters('ui', ['currentCategory', 'isAudioView', 'isPremiumContent']),

    ...mapState('user', ['isMember']),

    ...mapGetters('user', ['isUserSet']),

    ...mapGetters('favorites', ['indexInFavorites']),

    isCardView() {
      const { routeName } = this;
      return routeName === 'Card' || routeName === 'AudioCard';
    },

    isStackView() {
      const { routeName } = this;
      return routeName === 'Stack' || routeName === 'AudioStack';
    },

    secondaryColor() {
      const { currentCategory, routeName } = this;
      if (routeName === 'Visionaries') {
        return 'deepening';
      }
      return `${currentCategory}Secondary ${currentCategory === 'lightening' ? 'darken-2' : ''}`;
    },
  },

  methods: {
    back() {
      // TESTING Back button behavior, can remove logs later
      const { $fromRoute = {}, $router } = this;
      const { name = '' } = $fromRoute;
      // console.log('Back $fromRoute', $fromRoute);
      try {
        if (!name || name.includes('Auth') || name === 'AppHome') {
          $router.replace('/');
        } else {
          $router.go(-1);
        }
      } catch (error) {
        console.error('Router back error', error);
      }
    },

    toggleMode() {
      const { isAudioView, isStackView, currentId, $router } = this;
      let routeName;
      if (isAudioView) {
        routeName = isStackView ? 'Stack' : 'Card';
      } else {
        routeName = isStackView ? 'AudioStack' : 'AudioCard';
      }
      $router.replace({ name: routeName, params: { id: currentId } });
      this.$store.dispatch('ui/updateIsAudioDefaultMode', !isAudioView);
    },

    toggleFavorite() {
      this.$store.dispatch('favorites/toggleFavorite');
    },

    // Only needed for Stack add & edit
    // Can only access create & delete if subscriber
    handleShowModal(modal) {
      if (this.isMember) {
        this.showModal = modal;
      } else {
        const feature = modal === 'stack-add' ? 'stack_add_card' : 'stack_edit';
        this.$store.dispatch('ui/showPaywall', feature);
      }
    },
  },
};
</script>

<template>
  <div>
    <v-app-bar
      absolute
      flat
      :height="56 + safeArea"
      hide-on-scroll
      scroll-target="#main-app-container"
      scroll-threshold="10"
      class="top-bar"
    >
      <!-- LEFT SIDE -->
      <v-btn
        icon
        :retain-focus-on-click="false"
        :color="secondaryColor"
        class="icon-button back-button"
        aria-label="Back"
        @click="back"
      >
        <Back class="back-icon" />
      </v-btn>

      <v-spacer></v-spacer>

      <div v-if="isCardView || isStackView" class="mr-n3">
        <!-- Removing tooltips for now because show on every tap on iOS -->
        <!-- <v-tooltip bottom open-delay="250"> -->
        <!-- <template v-slot:activator="{ on, attrs }"> -->
        <!-- v-bind="attrs" v-on="on" -->
        <v-btn
          icon
          exact
          :retain-focus-on-click="false"
          :color="secondaryColor"
          :aria-label="isAudioView ? 'Reading Mode' : 'Audio toggleMode'"
          @click="toggleMode"
        >
          <CardText v-if="isAudioView" class="icon" />
          <Headphones v-else class="icon" />
        </v-btn>
        <!-- </template> -->
        <!-- <span>{{ isAudioView ? 'Read' : 'Listen' }}</span> -->
        <!-- </v-tooltip> -->

        <!-- <v-tooltip bottom open-delay="250">
          <template v-slot:activator="{ on, attrs }"> -->
        <v-btn
          icon
          :retain-focus-on-click="false"
          :color="secondaryColor"
          aria-label="Toggle Favorite"
          @click="toggleFavorite"
        >
          <Heart v-if="indexInFavorites === -1" class="icon" />
          <HeartFilled v-else class="icon" />
        </v-btn>
        <!-- </template>
          <span>{{ indexInFavorites === -1 ? 'Favorite' : 'Unfavorite' }}</span>
        </v-tooltip> -->

        <v-menu bottom left>
          <template v-slot:activator="{ on: onMenu }">
            <!-- Taking out tooltip for now because overlaps menu -->
            <!-- <v-tooltip bottom open-delay="250"> -->
            <!-- <template v-slot:activator="{ on: onTooltip, attrs }"> -->
            <v-btn
              icon
              :retain-focus-on-click="false"
              :color="secondaryColor"
              class="icon-button back-button"
              aria-label="More Options"
              v-on="{ ...onMenu }"
            >
              <!-- v-bind="attrs" v-on="{ ...onMenu, ...onTooltip }" -->
              <More class="icon" />
            </v-btn>
            <!-- </template> -->
            <!-- <span>More Options</span> -->
            <!-- </v-tooltip> -->
          </template>

          <v-list>
            <v-list-item
              v-if="isCardView"
              :ripple="{ class: `${currentCategory}--text` }"
              @click="handleShowModal('stack-add')"
            >
              <v-list-item-title>Add to playlist <LockIcon /></v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="isStackView"
              :ripple="{ class: `${currentCategory}--text` }"
              @click="handleShowModal('stack-edit')"
            >
              <v-list-item-title>Edit playlist <LockIcon /></v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="isStackView && currentId.substring(0, 5) === 'stack'"
              :ripple="{ class: `${currentCategory}--text` }"
              @click="showModal = 'stack-delete'"
            >
              <v-list-item-title>Delete playlist <LockIcon /></v-list-item-title>
            </v-list-item>

            <!-- <v-list-item to="/contact" :ripple="{ class: `${currentCategory}--text` }">
              <v-list-item-title>Send feedback</v-list-item-title>
            </v-list-item>-->

            <!-- <ShareContentMenuItem
              v-if="isCardView"
              :id="currentId"
              :is-premium="isPremiumContent"
              :current-category="currentCategory"
            /> -->
          </v-list>
        </v-menu>
      </div>

      <!-- Help Tooltips -->
      <transition name="fade" mode="out-in">
        <HelpTooltip
          v-if="routeName === 'Card' && helpTooltips.listenToggle"
          key="listenToggle"
          tooltip-id="listenToggle"
          :category="currentCategory"
          align="right"
          distance-from-top="calc(48px)"
          distance-from-side="calc(24px + 48px + 48px)"
        />

        <HelpTooltip
          v-else-if="routeName === 'AudioCard' && helpTooltips.readToggle"
          key="readToggle"
          tooltip-id="readToggle"
          :category="currentCategory"
          align="right"
          distance-from-top="calc(48px)"
          distance-from-side="calc(24px + 48px + 48px)"
        />

        <HelpTooltip
          v-else-if="routeName === 'Card' && helpTooltips.swipeCards && !helpTooltips.readToggle"
          key="swipeCards"
          tooltip-id="swipeCards"
          :category="currentCategory"
          align="center"
          distance-from-top="calc(100vh - 30vh)"
          distance-from-side="0"
          hide-arrow
        />
      </transition>
    </v-app-bar>

    <!-- MODALS -->
    <StackAddModal
      v-if="showModal === 'stack-add'"
      @new-stack="showModal = 'stack-create'"
      @close="showModal = ''"
    />

    <StackCreateModal
      v-if="showModal === 'stack-create'"
      :card-id="currentId"
      @close="showModal = ''"
    />

    <StackEditModal
      v-if="showModal === 'stack-edit'"
      :stack-id="currentId"
      @close="showModal = ''"
    />

    <StackDeleteModal
      v-if="showModal === 'stack-delete'"
      :stack-id="currentId"
      @close="showModal = ''"
    />
  </div>
</template>

<style lang="scss">
@import '@/styles/variables.scss';

.top-bar.v-app-bar {
  background-color: transparent !important;
  background: transparent;
  // background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.5) 80%);

  .v-toolbar__content {
    height: 56px !important;
    margin-top: var(--safe-area-inset-top);
    padding: 4px 20px !important;
  }

  .back-icon {
    height: 32px;
    width: 32px;
  }
}

.fade-enter-active {
  transition: opacity 300ms 700ms;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
